/**
 * Administration → Authorization Matrix (Day 11).
 *   Roles tab  — the grid: screens / reports / functions (rows, by module) × roles (columns);
 *                a module row toggles `<module>.*`, an item row an exact grant; "everything" per role.
 *   Users tab  — one user: roles, per-item overrides (inherit / allow / deny) with the effective
 *                decision and where it comes from, and the data scopes (companies, branches,
 *                projects, warehouses, cost centres) the user is confined to.
 * The server validates every code and enforces the decisions (see src/core/auth/access.ts).
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api.ts';
import { useQuery, invalidate } from '../../lib/hooks.ts';
import { useI18n } from '../../lib/i18n.tsx';
import { useMeta } from '../../lib/meta.tsx';
import { useSession } from '../../lib/session.tsx';
import { Link, useRoute } from '../../lib/router.tsx';
import { Badge, Button, Card, EmptyState, ErrorState, Spinner, Tabs, useToast } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { PageHead } from '../../components/PageHead.tsx';

type Kind = 'screen' | 'report' | 'function';
const KINDS: Kind[] = ['screen', 'report', 'function'];
interface Item { kind: Kind; code: string; module: string; label: string; labelAr: string; description: string | null }
interface Catalog { items: Item[]; modules: Array<{ code: string; label: string; labelAr: string }> }
type Grants = Record<Kind, string[]>;
interface RoleCol { id: string; code: string; name: string; nameAr: string | null; isSystem: boolean; members: number; grants: Grants }
interface Override { kind: Kind; code: string; effect: 'allow' | 'deny' }
interface Effective extends Item { allowed: boolean; viaRoles: string[]; override: 'allow' | 'deny' | null; owner: boolean }
interface ScopeGroup { dimension: string; label: string; labelAr: string; records: Array<{ id: string; label: string }> }
interface UserAccess { user: { membershipId: string; email: string; fullName: string; isOwner: boolean; status: string }; roles: Array<{ id: string; code: string; name: string }>; overrides: Override[]; scopes: ScopeGroup[]; modules: Catalog['modules']; effective: Effective[] }
interface TenantUser { membership_id: string; user_id: string; email: string; full_name: string; is_owner: boolean; job_title: string | null; roles: Array<{ code: string; name: string }> }

const matches = (pattern: string, code: string) => pattern === '*' || pattern === code || (pattern.endsWith('.*') && code.startsWith(pattern.slice(0, -1)));

export function AuthorizationPage() {
  const { t } = useI18n();
  const { nav } = useMeta();
  const { query, setQuery } = useRoute();
  const tab = (query.get('tab') ?? 'roles') as 'roles' | 'users';
  const admin = nav.find((m) => m.code === 'admin');
  return (
    <div className="stack">
      <PageHead title={t('admin.authorization')} sub={t('admin.authorizationHint')} crumbs={[{ label: admin?.label ?? 'Administration', to: '/admin' }]} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v === 'roles' ? null : v, user: null })} tabs={[{ value: 'roles', label: t('admin.matrixRoles') }, { value: 'users', label: t('admin.matrixUsers') }]} />
      {tab === 'roles' ? <RoleMatrix /> : <UserMatrix />}
    </div>
  );
}

// ----------------------------------------------------------------------------- filters shared by both tabs
function useItemFilter(items: Item[], modules: Catalog['modules']) {
  const { t, locale } = useI18n();
  const [kind, setKind] = useState<Kind>('screen');
  const [module, setModule] = useState('');
  const [search, setSearch] = useState('');
  const label = (i: { label: string; labelAr: string }) => (locale === 'ar' && i.labelAr ? i.labelAr : i.label);
  const shown = useMemo(() => items.filter((i) => i.kind === kind && (!module || i.module === module) && (!search || `${i.code} ${i.label} ${i.labelAr}`.toLowerCase().includes(search.toLowerCase()))), [items, kind, module, search]);
  const controls = (
    <div className="toolbar" style={{ gap: 8 }}>
      <Tabs value={kind} onChange={setKind} tabs={KINDS.map((k) => ({ value: k, label: t(`admin.kind_${k}`), count: items.filter((i) => i.kind === k).length }))} />
      <select className="input sm" value={module} onChange={(e) => setModule(e.target.value)} style={{ width: 200 }}>
        <option value="">{t('admin.allModules')}</option>
        {modules.map((m) => <option key={m.code} value={m.code}>{label(m)}</option>)}
      </select>
      <div className="search" style={{ maxWidth: 260 }}><Icon name="Search" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common.searchPlaceholder')} style={{ border: 0, background: 'transparent', font: 'inherit', width: '100%', outline: 'none', color: 'var(--fg)' }} /></div>
    </div>
  );
  return { kind, module, shown, controls, label };
}

// ----------------------------------------------------------------------------- roles × items
function RoleMatrix() {
  const { t } = useI18n();
  const toast = useToast();
  const { can } = useSession();
  const data = useQuery('admin:authz:roles', () => api.get<{ catalog: Catalog; roles: RoleCol[] }>('admin/authorization/roles'), { ttlMs: 0 });
  const [draft, setDraft] = useState<Record<string, Grants>>({});     // roleId → grants being edited
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data.data) setDraft(Object.fromEntries(data.data.roles.map((r) => [r.id, { screen: [...r.grants.screen], report: [...r.grants.report], function: [...r.grants.function] }]))); }, [data.data]);
  const items = data.data?.catalog.items ?? [];
  const modules = data.data?.catalog.modules ?? [];
  const { kind, module, shown, controls, label } = useItemFilter(items, modules);
  const canEdit = can('admin.authorization.configure');
  const canEverything = can('admin.role.configure');
  if (data.error) return <ErrorState error={data.error} retry={() => void data.refresh()} />;
  if (!data.data) return <Spinner />;
  const roles = data.data.roles;
  const grantsOf = (r: RoleCol) => draft[r.id] ?? r.grants;
  const dirty = roles.filter((r) => JSON.stringify(grantsOf(r)) !== JSON.stringify(r.grants));
  const set = (roleId: string, k: Kind, codes: string[]) => setDraft((d) => ({ ...d, [roleId]: { ...(d[roleId] ?? { screen: [], report: [], function: [] }), [k]: codes } }));
  const toggleItem = (r: RoleCol, item: Item) => {
    const g = grantsOf(r)[kind];
    set(r.id, kind, g.includes(item.code) ? g.filter((c) => c !== item.code) : [...g, item.code]);
  };
  const toggleModule = (r: RoleCol, mod: string) => {
    const g = grantsOf(r)[kind]; const pattern = `${mod}.*`;
    set(r.id, kind, g.includes(pattern) ? g.filter((c) => c !== pattern) : [...g.filter((c) => !c.startsWith(`${mod}.`)), pattern]);
  };
  const toggleAll = (r: RoleCol) => { const g = grantsOf(r)[kind]; set(r.id, kind, g.includes('*') ? g.filter((c) => c !== '*') : ['*']); };
  const save = async () => {
    setBusy(true);
    try {
      for (const r of dirty) await api.put(`admin/authorization/roles/${r.id}`, { grants: grantsOf(r), reason: 'Authorization matrix edited in administration' });
      toast.success(t('common.saved')); invalidate('admin:authz'); await data.refresh();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const byModule = new Map<string, Item[]>();
  for (const i of shown) { if (!byModule.has(i.module)) byModule.set(i.module, []); byModule.get(i.module)!.push(i); }
  const editable = (r: RoleCol) => canEdit && r.code !== 'system_administrator';
  return (
    <Card flush>
      <div className="card-b" style={{ paddingBottom: 0 }}>{controls}</div>
      <div className="table-wrap" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table className="matrix authz" data-kind={kind}>
          <thead>
            <tr>
              <th className="row-h">{t(`admin.kind_${kind}`)}</th>
              {roles.map((r) => <th key={r.id} title={`${r.code} · ${r.members} ${t('admin.members')}`}><div className="role-h">{label({ label: r.name, labelAr: r.nameAr ?? r.name })}</div>{r.isSystem && <Icon name="Lock" size={10} className="muted" />}</th>)}
            </tr>
            <tr>
              <td className="row-h muted">{t('admin.everything')}</td>
              {roles.map((r) => { const all = grantsOf(r)[kind].includes('*'); return <td key={r.id} className={all ? 'granted-wild' : ''}><input type="checkbox" data-role={r.code} data-all checked={all} disabled={!editable(r) || (!all && !canEverything)} onChange={() => toggleAll(r)} title={canEverything ? '*' : t('admin.everythingNeedsConfigure')} /></td>; })}
            </tr>
          </thead>
          <tbody>
            {[...byModule.entries()].map(([mod, its]) => (<>
              <tr key={`m-${mod}`}>
                <th className="mod">{label(modules.find((m) => m.code === mod) ?? { label: mod, labelAr: mod })} <span className="muted">· {mod}.*</span></th>
                {roles.map((r) => { const g = grantsOf(r)[kind]; const all = g.includes('*'); const wild = g.includes(`${mod}.*`); return <td key={r.id} className={`mod ${all || wild ? 'granted-wild' : ''}`}><input type="checkbox" data-role={r.code} data-module={mod} checked={all || wild} disabled={!editable(r) || all} onChange={() => toggleModule(r, mod)} /></td>; })}
              </tr>
              {its.map((item) => (
                <tr key={item.code}>
                  <td className="row-h" title={item.description ?? item.code}>{label(item)} <span className="muted" style={{ fontWeight: 400 }}>{item.code}</span></td>
                  {roles.map((r) => {
                    const g = grantsOf(r)[kind]; const exact = g.includes(item.code); const wild = g.some((p) => p !== item.code && matches(p, item.code));
                    return <td key={r.id} className={wild ? 'granted-wild' : ''} title={wild ? `${t('admin.viaPattern')} ${g.find((p) => p !== item.code && matches(p, item.code))}` : item.code}><input type="checkbox" data-role={r.code} data-code={item.code} checked={exact || wild} disabled={!editable(r) || wild} onChange={() => toggleItem(r, item)} /></td>;
                  })}
                </tr>
              ))}
            </>))}
            {shown.length === 0 && <tr><td colSpan={roles.length + 1}><EmptyState title={t('common.noData')} /></td></tr>}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="form-actions static">
          <span className="muted">{dirty.length ? t('admin.unsavedRoles', { n: dirty.length }) : t('admin.matrixLegend')}</span>
          <span className="spacer" />
          <Button onClick={() => setDraft({})} disabled={!dirty.length}>{t('common.cancel')}</Button>
          <Button variant="primary" icon="Save" busy={busy} disabled={!dirty.length} onClick={() => void save()} data-save-matrix>{t('common.save')}</Button>
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------------- one user
function UserMatrix() {
  const { t } = useI18n();
  const { query, setQuery } = useRoute();
  const membershipId = query.get('user');
  const [search, setSearch] = useState('');
  const users = useQuery(`admin:authz:users:${search}`, () => api.get<{ rows: TenantUser[]; total: number }>('admin/users', { page: 1, pageSize: 50, search: search || undefined }), { ttlMs: 0 });
  return (
    <div className="grid" style={{ gridTemplateColumns: 'minmax(240px, 1fr) 3fr', gap: 16, alignItems: 'start' }}>
      <Card title={t('admin.users')}>
        <div className="search" style={{ marginBottom: 8 }}><Icon name="Search" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common.searchPlaceholder')} style={{ border: 0, background: 'transparent', font: 'inherit', width: '100%', outline: 'none', color: 'var(--fg)' }} /></div>
        {!users.data ? <Spinner /> : (
          <ul className="list" data-user-list>
            {users.data.rows.map((u) => (
              <li key={u.membership_id} className={u.membership_id === membershipId ? 'active' : ''}>
                <a href="#" onClick={(e) => { e.preventDefault(); setQuery({ user: u.membership_id }); }} data-user={u.email}>
                  <strong>{u.full_name}</strong> {u.is_owner && <Badge color="green">{t('admin.owner')}</Badge>}
                  <div className="muted" style={{ fontSize: 12 }}>{u.email} · {u.roles.map((r) => r.name).join(', ') || '—'}</div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {membershipId ? <UserDetail membershipId={membershipId} /> : <Card><EmptyState title={t('admin.pickUser')} hint={t('admin.pickUserHint')} /></Card>}
    </div>
  );
}

function UserDetail({ membershipId }: { membershipId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const { can } = useSession();
  const canEdit = can('admin.authorization.configure');
  const data = useQuery(`admin:authz:user:${membershipId}`, () => api.get<UserAccess>(`admin/authorization/users/${membershipId}`), { ttlMs: 0 });
  const choices = useQuery('admin:authz:scope-choices', () => api.get<ScopeGroup[]>('admin/authorization/scope-choices'), { ttlMs: 60_000 });
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [scopes, setScopes] = useState<Record<string, string[] | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    if (!data.data) return;
    setOverrides(data.data.overrides.map((o) => ({ ...o })));
    setScopes(Object.fromEntries((choices.data ?? []).map((g) => [g.dimension, data.data!.scopes.find((s) => s.dimension === g.dimension)?.records.map((r) => r.id) ?? null])));
  }, [data.data, choices.data]);
  const items: Item[] = data.data?.effective ?? [];
  const { kind, shown, controls, label } = useItemFilter(items, data.data?.modules ?? []);
  if (data.error) return <ErrorState error={data.error} retry={() => void data.refresh()} />;
  if (!data.data) return <Spinner />;
  const u = data.data;
  const overrideOf = (item: Item) => overrides.find((o) => o.kind === item.kind && o.code === item.code)?.effect ?? '';
  const setOverride = (item: Item, effect: '' | 'allow' | 'deny') => setOverrides((list) => { const rest = list.filter((o) => !(o.kind === item.kind && o.code === item.code)); return effect ? [...rest, { kind: item.kind, code: item.code, effect }] : rest; });
  const dirtyOverrides = JSON.stringify([...overrides].sort((a, b) => a.code.localeCompare(b.code))) !== JSON.stringify([...u.overrides].sort((a, b) => a.code.localeCompare(b.code)));
  const savedScopes = Object.fromEntries((choices.data ?? []).map((g) => [g.dimension, u.scopes.find((s) => s.dimension === g.dimension)?.records.map((r) => r.id) ?? null]));
  const dirtyScopes = JSON.stringify(scopes) !== JSON.stringify(savedScopes);
  const save = async (what: 'overrides' | 'scopes') => {
    setBusy(what);
    try {
      await api.put(`admin/authorization/users/${membershipId}/${what}`, what === 'overrides' ? { overrides, reason: 'User overrides edited in administration' } : { scopes, reason: 'User data scopes edited in administration' });
      toast.success(t('common.saved')); await data.refresh();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  // the effective decision as it would be after the pending overrides (before saving)
  const effectiveNow = (item: Effective) => { const o = overrideOf(item); if (o === 'deny') return false; if (o === 'allow') return true; return item.viaRoles.length > 0 || (item.owner && !item.override); };
  return (
    <div className="stack">
      <Card title={<>{u.user.fullName} <span className="muted" style={{ fontWeight: 400 }}>· {u.user.email}</span></>}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {u.user.isOwner && <Badge color="green">{t('admin.owner')}</Badge>}
          {u.roles.map((r) => <Badge key={r.id}>{r.name}</Badge>)}
          {!u.roles.length && <span className="muted">{t('admin.noRoles')}</span>}
          <span className="spacer" />
          <Link to={`/admin/users`} className="btn sm">{t('admin.users')}</Link>
        </div>
        {u.user.isOwner && <div className="banner amber" style={{ marginTop: 10 }}>{t('admin.ownerNote')}</div>}
      </Card>

      <Card title={t('admin.dataScopes')} flush>
        <div className="card-b muted" style={{ fontSize: 13 }}>{t('admin.dataScopesHint')}</div>
        {!choices.data ? <Spinner /> : choices.data.map((g) => {
          const current = scopes[g.dimension];
          const unrestricted = current === null || current === undefined;
          return (
            <div key={g.dimension} className="card-b" style={{ borderTop: '1px solid var(--border)' }} data-scope={g.dimension}>
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                <strong style={{ minWidth: 140 }}>{label(g)}</strong>
                <label className="check"><input type="checkbox" checked={unrestricted} disabled={!canEdit} onChange={(e) => setScopes((s) => ({ ...s, [g.dimension]: e.target.checked ? null : [] }))} /><span>{t('admin.unrestricted')}</span></label>
                {!unrestricted && <span className="muted">{t('admin.confinedTo', { n: current!.length })}</span>}
              </div>
              {!unrestricted && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {g.records.map((r) => { const on = current!.includes(r.id); return <label key={r.id} className={`chip ${on ? 'on' : ''}`}><input type="checkbox" checked={on} disabled={!canEdit} onChange={(e) => setScopes((s) => ({ ...s, [g.dimension]: e.target.checked ? [...(s[g.dimension] ?? []), r.id] : (s[g.dimension] ?? []).filter((x) => x !== r.id) }))} /> {r.label}</label>; })}
                  {!g.records.length && <span className="muted">{t('common.noData')}</span>}
                </div>
              )}
            </div>
          );
        })}
        {canEdit && <div className="form-actions static"><span className="spacer" /><Button variant="primary" icon="Save" busy={busy === 'scopes'} disabled={!dirtyScopes} onClick={() => void save('scopes')} data-save-scopes>{t('admin.saveScopes')}</Button></div>}
      </Card>

      <Card title={t('admin.effectiveAccess')} flush>
        <div className="card-b" style={{ paddingBottom: 0 }}>{controls}</div>
        <div className="table-wrap" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <table className="table" data-kind={kind}>
            <thead><tr><th>{t(`admin.kind_${kind}`)}</th><th>{t('admin.effective')}</th><th>{t('admin.grantedVia')}</th><th style={{ width: 160 }}>{t('admin.override')}</th></tr></thead>
            <tbody>
              {shown.map((item) => {
                const e = item as Effective; const on = effectiveNow(e);
                return (
                  <tr key={item.code} data-code={item.code} data-allowed={on ? '1' : '0'}>
                    <td><div>{label(item)}</div><div className="muted" style={{ fontSize: 12 }}>{item.code}</div></td>
                    <td>{on ? <Badge color="green">{t('admin.allowed')}</Badge> : <Badge color="red">{t('admin.refused')}</Badge>}</td>
                    <td className="muted">{overrideOf(item) ? t(`admin.override_${overrideOf(item)}`) : e.viaRoles.length ? e.viaRoles.join(', ') : e.owner ? t('admin.owner') : '—'}</td>
                    <td>
                      <select className="input sm" value={overrideOf(item)} disabled={!canEdit} onChange={(ev) => setOverride(item, ev.target.value as '' | 'allow' | 'deny')}>
                        <option value="">{t('admin.override_inherit')}</option><option value="allow">{t('admin.override_allow')}</option><option value="deny">{t('admin.override_deny')}</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={4}><EmptyState title={t('common.noData')} /></td></tr>}
            </tbody>
          </table>
        </div>
        {canEdit && <div className="form-actions static"><span className="muted">{t('admin.overrideHint')}</span><span className="spacer" /><Button variant="primary" icon="Save" busy={busy === 'overrides'} disabled={!dirtyOverrides} onClick={() => void save('overrides')} data-save-overrides>{t('admin.saveOverrides')}</Button></div>}
      </Card>
    </div>
  );
}
